import React, { useEffect, useRef, useState } from 'react';
import { Offcanvas } from 'bootstrap';
import { IServiceFactory } from '../../services/IServiceFactory';
import Table from 'datatables.net-bs5';
import DataTable, { DataTableRef } from 'datatables.net-react';
import { ConfirmModal } from '../../components/ConfirmModal';
import { InstanceForm } from '../../components/InstanceForm';
import { ToastContainer } from '../../components/ToastContainer';
import { ToastMessage, ToastMessageType } from '../../model/ToastMessage';
import { Instance } from '../../model/Instance';
import { Spinner } from '../../components/Spinner';
import { InstanceSettings } from '../../model/InstanceSettings';
import { getSettings, saveSettings } from '../../services/StudioService';
DataTable.use(Table);

const Instances = ({ serviceFactory}: Props) => {
  const [instances, setInstances] = useState<Instance[]>([]);
  const [selectedInstance, setSelectedInstance] = useState<Instance | undefined>();
  const [selectedInstanceSettings, setSelectedInstanceSettings] = useState<InstanceSettings | undefined>();
  const [confirmModalMessage, setConfirmModalMessage] = useState<string[]>([]);
  const [toastMessages, setToastMessages] = useState<ToastMessage[]>([]);
  const [loading, setLoading] = useState(false);
  const offCanvasRef = useRef<HTMLDivElement | null>(null);
  const tableRef = useRef<DataTableRef | null>(null);

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      const instances = await serviceFactory.getInstanceService().all();
      setInstances(instances);
      setLoading(false);
    };
    load();
    if(offCanvasRef.current) {
      Offcanvas.getOrCreateInstance(offCanvasRef.current, { backdrop: true });
    }
  }, []);

  const onNew = () => {
    setSelectedInstance({ name: '', description: '', apiKey: '' });
    Offcanvas.getInstance(offCanvasRef.current).show();
  }

  const onSelectInstance = async (instance: Instance) => {
    setSelectedInstance(instance);
    const settings = await getSettings(instance);
    setSelectedInstanceSettings(settings);
    Offcanvas.getInstance(offCanvasRef.current).show();
  }

  const onShowDeleteModal = (instance: Instance) => {
    setSelectedInstance(instance);
    setConfirmModalMessage([ `Are you sure you want to delete instance ${instance.name}?` ]);
  }

  const onSave = async (instance: Instance, settings: InstanceSettings) => {
    Offcanvas.getInstance(offCanvasRef.current).hide();
    try {
      setLoading(true);
      const service = serviceFactory.getInstanceService();
      if(instance.id) {
        selectedInstance.name = instance.name;
        selectedInstance.description = instance.description;
        selectedInstance.apiKey = instance.apiKey;
        await service.update(instance.id, instance);
      }
      else {
        await service.create(instance); 
        instances.push(instance);
      }
      await saveSettings(instance, settings);
      setInstances([ ...instances ]);
      setSelectedInstance(undefined);
      setToastMessages([ { type: ToastMessageType.success, message: 'Instance saved successfully', title: 'Success' } ]);
    }
    catch(e) {
      console.log(e);
      setToastMessages([ { type: ToastMessageType.error, message: 'An error occurred while saving the instance', title: 'Error' } ]);
    }
    finally {
      setLoading(false);
    }
  }

  const onDelete = async () => {
    if(selectedInstance) {
      try {
        await serviceFactory.getInstanceService().delete(selectedInstance);
        setInstances(instances.filter(i => i.id !== selectedInstance.id));
        setSelectedInstance(undefined);
      }
      catch(e) {
        console.log(e);
      }
    }
  }

  const onCloseOffCanvas = () => {
    setSelectedInstance(undefined);
    Offcanvas.getInstance(offCanvasRef.current).hide();
  }

  const order: { name: string; dir: 'asc' | 'desc'; } = { name: 'name', dir: 'asc' };

  const options = {
    language: {
      info: 'Showing page _PAGE_ of _PAGES_',
      infoEmpty: 'No instances to display',
      infoFiltered: '(filtered from a total of _MAX_ instances)',
      lengthMenu: '',
      zeroRecords: 'No instance to display',
      search: '',
      searchPlaceholder: 'Search'
    },
    order: order,
  };
  const columns = [
    { data: 'name' },
    { data: 'description' },
    { data: 'id', searchable: false, orderable: false }
  ];
  const slots = {
    2: (id: string, instance: Instance) => {
      return (
        <div className="d-flex">
          <a className="text-dark" onClick={() => onSelectInstance(instance)}>
            <i className="icon-base ti ti-pencil me-1"></i>
          </a>
          <a className="text-dark" onClick={() => onShowDeleteModal(instance)}>
            <i className="icon-base ti ti-trash me-1"></i>
          </a>
        </div>
      );
    }
  };
  return (
    <>
      <h5 className="pb-3 mb-3 border-bottom">Instances</h5>
      <div className="mb-3 pb-4 border-bottom d-flex align-items-center justify-content-end">
        <button type="button" className="btn btn-primary" onClick={onNew}>
          <i className="ti ti-new-section me-1"></i>
          Add Instance
        </button>
      </div>
      <DataTable ref={tableRef} className="table border-top mt-2" data={instances} options={options} columns={columns} slots={slots}>
        <thead>
          <tr>
            <th>NAME</th>
            <th>DESCRIPTION</th>
            <th>ACTION</th>
          </tr>
        </thead>
      </DataTable>
      <div ref={offCanvasRef} className="offcanvas offcanvas-end" tabIndex={-1} id="offcanvas">
        <div className="offcanvas-header">
          <h5 className="offcanvas-title">{selectedInstance && selectedInstance.id ? 'Edit' : 'Add'} Instance</h5>
          <button type="button" className="btn-close bg-white text-reset" data-bs-dismiss="offcanvas" aria-label="Close"></button>
        </div>
        <div className="offcanvas-body mx-0 h-100 flex-grow-0">
          {selectedInstance && <InstanceForm instance={selectedInstance} settings={selectedInstanceSettings} onSave={onSave} onCancel={onCloseOffCanvas} />}
        </div>
      </div>
      <ConfirmModal title="Confirm" message={confirmModalMessage} confirmLabel="OK" cancelLabel="Cancel" confirmCallback={onDelete} cancelCallback={() => setConfirmModalMessage([])} />
      <ToastContainer messages={toastMessages} />
      <Spinner loading={loading} />
    </>
  );
}

interface Props {
  serviceFactory: IServiceFactory
}

export { Instances };